import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { ChatConversationAgent, ChatAttachmentInfo } from '../../services/chat-api.js';
import { getAttachmentUrl } from '../../services/chat-api.js';

export interface AgentChatMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  source?: 'text' | 'voice';
  isToolCall?: boolean;
  created_at?: string;
  attachments?: ChatAttachmentInfo[];
}

@customElement('cp-agent-chat-message')
export class CpAgentChatMessage extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) message!: AgentChatMessage;
  @property({ type: Boolean }) isStreaming = false;
  @property({ type: Object }) agent: ChatConversationAgent | null = null;

  private _formatTime(iso?: string): string {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private _wrapTables(htmlStr: string): string {
    return htmlStr.replace(
      /<table>/g,
      '<div class="cp-agent-chat-message__table-wrap"><table>'
    ).replace(
      /<\/table>/g,
      '</table></div>'
    );
  }

  private _isImage(mime: string): boolean {
    return mime.startsWith('image/');
  }

  private _formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private _renderAttachments(attachments: ChatAttachmentInfo[]) {
    if (!attachments || attachments.length === 0) return nothing;

    return html`
      <div class="cp-agent-chat-message__attachments">
        ${attachments.map(att => {
          const url = getAttachmentUrl(att.id);
          if (this._isImage(att.mime_type)) {
            return html`
              <a class="cp-agent-chat-message__att-img-link" href="${url}" target="_blank" rel="noopener">
                <img
                  class="cp-agent-chat-message__att-img"
                  src="${url}"
                  alt="${att.file_name}"
                  loading="lazy"
                />
              </a>
            `;
          }
          return html`
            <a class="cp-agent-chat-message__att-file" href="${url}" target="_blank" rel="noopener" download="${att.file_name}">
              <svg class="cp-agent-chat-message__att-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <span class="cp-agent-chat-message__att-file-name">${att.file_name}</span>
              <span class="cp-agent-chat-message__att-file-size">${this._formatFileSize(att.file_size)}</span>
            </a>
          `;
        })}
      </div>
    `;
  }

  private _renderContent() {
    const { message, isStreaming } = this;
    if (message.role === 'user') {
      return html`
        <div class="cp-agent-chat-message__content">${message.content}</div>
        ${this._renderAttachments(message.attachments ?? [])}
      `;
    }
    // Agent: Markdown rendering via marked
    const rawHtml = marked.parse(message.content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    const rendered = this._wrapTables(sanitized);
    return html`
      <div class="cp-agent-chat-message__content cp-agent-chat-message__content--markdown">
        ${unsafeHTML(rendered)}${isStreaming ? html`<span class="cp-agent-chat-message__cursor"></span>` : nothing}
      </div>
    `;
  }

  private _renderAgentAvatar() {
    if (!this.agent) return nothing;

    if (this.agent.has_avatar) {
      return html`
        <img
          class="cp-agent-chat-message__avatar"
          src="/api/agents/${this.agent.id}/avatar"
          alt="${this.agent.name}"
          width="24"
          height="24"
        />
      `;
    }

    // Color dot fallback
    return html`
      <span
        class="cp-agent-chat-message__avatar-dot"
        style="background: ${this.agent.color}"
      ></span>
    `;
  }

  render() {
    const { message } = this;
    if (!message) return html``;

    const isUser = message.role === 'user';
    const isAssistant = !isUser;
    const roleLabel = isUser ? 'Du' : (this.agent?.name ?? 'Agent');
    const timeStr = this._formatTime(message.created_at);
    const accentColor = isAssistant && this.agent?.color ? this.agent.color : null;

    return html`
      <div
        class="cp-agent-chat-message ${isUser ? 'cp-agent-chat-message--user' : 'cp-agent-chat-message--assistant'}"
        style=${accentColor ? `--agent-color: ${accentColor}` : ''}
      >
        <div class="cp-agent-chat-message__header">
          ${isAssistant ? this._renderAgentAvatar() : nothing}
          <span
            class="cp-agent-chat-message__role"
            style=${accentColor ? `color: ${accentColor}` : ''}
          >${roleLabel}</span>
          ${timeStr ? html`<span class="cp-agent-chat-message__time">${timeStr}</span>` : nothing}
          ${message.source === 'voice' ? html`<span class="cp-agent-chat-message__badge cp-agent-chat-message__badge--voice">Voice</span>` : nothing}
          ${message.isToolCall ? html`<span class="cp-agent-chat-message__badge cp-agent-chat-message__badge--tool">Tool</span>` : nothing}
        </div>
        <div class="cp-agent-chat-message__bubble">
          ${this._renderContent()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-chat-message': CpAgentChatMessage;
  }
}
